import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../lib/api.js'
import { normalizeSchools } from '../../lib/schools.js'

/**
 * Debounced typeahead search against GET /api/hubspot/schools?q=.
 *
 * - Debounces input (default 250ms) so we don't hammer the backend.
 * - Cancels the in-flight request on each new keystroke via AbortController,
 *   so results never arrive out of order.
 * - Returns normalized school objects (see lib/schools.js).
 *
 * @param {string} query
 * @param {{ minChars?: number, delay?: number }} [opts]
 */
export function useSchoolSearch(query, { minChars = 2, delay = 250 } = {}) {
  const [results, setResults] = useState([])
  const [status, setStatus] = useState('idle') // idle | loading | done | error
  const [error, setError] = useState('')
  const abortRef = useRef(null)

  useEffect(() => {
    const q = query.trim()

    // Below threshold: clear and stop.
    if (q.length < minChars) {
      if (abortRef.current) abortRef.current.abort()
      setResults([])
      setStatus('idle')
      setError('')
      return
    }

    setStatus('loading')
    const handle = setTimeout(async () => {
      // Cancel any previous request.
      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller

      try {
        const res = await api.schools(q, { signal: controller.signal })
        if (controller.signal.aborted) return
        setResults(normalizeSchools(res))
        setStatus('done')
        setError('')
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return
        setResults([])
        setStatus('error')
        setError(err?.message || 'Search failed.')
      }
    }, delay)

    return () => clearTimeout(handle)
  }, [query, minChars, delay])

  // Cleanup on unmount.
  useEffect(() => () => abortRef.current?.abort(), [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setResults([])
    setStatus('idle')
    setError('')
  }, [])

  return { results, status, error, reset }
}
